import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import { login } from '../services/identity';
import { useAuth } from '../context/AuthContext';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import Alert from '@mui/material/Alert';

interface LocationState {
  from?: {
    pathname: string;
  };
}

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Get the page user was trying to access before being redirected to login
  const from = (location.state as LocationState)?.from?.pathname || '/menu';

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    
    const data = new FormData(event.currentTarget);
    const email = String(data.get('email') || '');
    const password = String(data.get('password') || '');

    try {
      const response = await login({
        email: email,
        password: password,
        rememberMe: true,
        lockoutOnFailure: true
      });
      
      if (response.succeeded && response.jwtToken) {
        authLogin(response.jwtToken);
        // Navigate to the page user was trying to access, or /menu by default
        navigate(from, { replace: true });
      } else {
        setError('Přihlášení selhalo. Zkontrolujte své přihlašovací údaje.');
      }
    } catch (error) {
      console.error(error);
      setError('Při přihlašování došlo k chybě. Zkuste to prosím znovu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 3,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Avatar sx={{ m: 'auto', mb: 2, bgcolor: 'primary.main', width: 56, height: 56 }}>
              <RestaurantIcon fontSize="large" />
            </Avatar>
            <Typography component="h1" variant="h5" fontWeight={600}>
              Přihlášení
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
              Vítejte zpět! Přihlaste se ke svému účtu.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="E-mail"
              name="email"
              autoComplete="email"
              autoFocus
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Heslo"
              type="password"
              id="password"
              autoComplete="current-password"
            />
            <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Zapamatuj si mě"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Přihlásit'}
            </Button>
            <Grid container>
              <Grid item xs>
                <Link href="#" variant="body2" color="text.secondary">
                  Zapomněli jste heslo?
                </Link>
              </Grid>
              <Grid item>
                <Link component={RouterLink} to="/register" variant="body2">
                  Nemáte účet? Registrujte se
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}